import React, {useEffect, useState} from "react"
import {
    Alert,
    Box,
    Button, Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Divider,
    FormControl, Grid2 as Grid,
    IconButton,
    InputLabel, MenuItem, Select, Stack, TextField, Typography
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {PaperLabelled} from "@/components/UI";
import {useData} from "@/context/PackagingDataProvider";

const MIN_DIMX = 0.395
const MAX_DIMX = 1

export default function DownloadDialog(props: any) {

    const {open, onClose, product, products, t} = props
    const {downloadSingleQRCode, downloadQRCodesBatch} = useData()

    // Bulk mode: `products` (the table's checkbox selection) takes precedence
    // over the single `product`. A mixed Standard/GS1 selection falls back to
    // the classic download for every code — GS1 options (Dim-X) are not
    // applicable to the whole set.
    const bulkProducts: any[] = Array.isArray(products) ? products : []
    const isBulk = bulkProducts.length > 0
    const gs1Count = bulkProducts.filter((p: any) => p?.type === "GS1").length
    const isMixedSelection = isBulk && gs1Count > 0 && gs1Count < bulkProducts.length
    const allGs1 = isBulk && gs1Count === bulkProducts.length

    let isGs1Product = isBulk ? allGs1 : (!!product && product.type === "GS1")

    const [downloadType, setDownloadType] = useState<"classic" | "gs1">("classic")
    const [file, setFile] = useState<any>({
        format: "png",
        unit: "px",
        size: "300",
        resolution: "75",
        real_size: false,
        dim_x: 0.420
    })

    // reset download type — a bulk all-GS1 selection auto-selects the GS1
    // download, everything else starts on classic
    useEffect(() => {
        setDownloadType(open && allGs1 ? "gs1" : "classic")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // stop people from giving weird numbers
    useEffect(() => {
        if (file.dim_x > MAX_DIMX) setFile({...file, dim_x: MAX_DIMX})
        else if (file.dim_x < MIN_DIMX) setFile({...file, dim_x: MIN_DIMX})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    useEffect(() => {
        if (downloadType === "gs1") setFile({...file, format: "svg", real_size: true})
        else setFile({...file, format: "png", real_size: false})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadType]);

    const isCorrectlySized =
        (file.unit === "px" && (Number(file.size) >= 100 && Number(file.size) <= 2500)) ||
        (file.unit === "mm" && file.size >= 30 && (
            (file.resolution === "75" && Number(file.size) <= 847) || (file.resolution === "300" && Number(file.size) <= 212)
        ))


    // Download
    const downloadQRCode = async () => {
        if (isBulk) {
            await downloadQRCodesBatch(bulkProducts.map((p: any) => p.qr_code.id), file, "files").then(() => {
                onClose()
            })
        } else {
            await downloadSingleQRCode(product.qr_code.id, product.name, file, product.gtin).then(() => {
                onClose()
            })
        }
    }

    // Sections
    const renderFileFormatSelection = () => {

        const renderDownloadTypeSelector = () => {
            return (
                <>
                    <Button
                        variant={downloadType === "classic" ? "contained" : "outlined"}
                        disableElevation
                        onClick={() => setDownloadType("classic")}
                        sx={{ mr: 1 }}
                    >
                        {t("classic_download")}
                    </Button>
                    <Button
                        variant={downloadType === "gs1" ? "contained" : "outlined"}
                        disableElevation
                        onClick={() => setDownloadType("gs1")}
                    >
                        {t("gs1_download")}
                    </Button>
                </>
            )
        }

        const renderTypeSelection = () => {
            return (
                <FormControl fullWidth>
                    <InputLabel id={"file-format"}>{t("file_format")}</InputLabel>
                    <Select
                        label={t("file_format")}
                        labelId={"file-format"}
                        value={file.format}
                        onChange={(e) => setFile({...file, format: e.target.value})}
                    >
                        <MenuItem value={"png"}>PNG</MenuItem>
                        <MenuItem value={"jpeg"}>JPEG</MenuItem>
                        <MenuItem value={"svg"}>SVG</MenuItem>
                        <MenuItem value={"pdf"}>PDF</MenuItem>
                    </Select>
                </FormControl>
            )
        }

        const renderGs1FormatSelection = () => {
            // GS1 codes are certified vector output: SVG, or a PDF carrying
            // the same vector content (pdf_content=vector server-side).
            return (
                <FormControl fullWidth>
                    <InputLabel id={"gs1-file-format"}>{t("file_format")}</InputLabel>
                    <Select
                        label={t("file_format")}
                        labelId={"gs1-file-format"}
                        value={file.format}
                        onChange={(e) => {
                            const format = e.target.value
                            setFile({...file, format, pdf_content: format === "pdf" ? "vector" : undefined})
                        }}
                    >
                        <MenuItem value={"svg"}>SVG</MenuItem>
                        <MenuItem value={"pdf"}>{t("gs1_pdf_option")}</MenuItem>
                    </Select>
                </FormControl>
            )
        }

        const renderPdfContentSelection = () => {
            // What the PDF embeds: raster PNG (default) or the generator's
            // true vector page (print quality).
            return (
                <FormControl fullWidth>
                    <InputLabel id={"pdf-content"}>{t("pdf_content")}</InputLabel>
                    <Select
                        label={t("pdf_content")}
                        labelId={"pdf-content"}
                        value={file.pdf_content ?? "raster"}
                        onChange={(e) => setFile({...file, pdf_content: e.target.value})}
                    >
                        <MenuItem value={"raster"}>{t("pdf_content_raster")}</MenuItem>
                        <MenuItem value={"vector"}>{t("pdf_content_vector")}</MenuItem>
                    </Select>
                </FormControl>
            )
        }

        const renderUnitSelection = () => {
            return (
                <FormControl fullWidth>
                    <InputLabel id={"file-unit"}>{t("file_unit")}</InputLabel>
                    <Select
                        label={t("file_unit")}
                        labelId={"file-unit"}
                        value={file.unit}
                        onChange={(e) => setFile({...file, unit: e.target.value})}
                    >
                        <MenuItem value={"px"}>{t("unit_pixels")}</MenuItem>
                        <MenuItem value={"mm"}>{t("unit_millimeters")}</MenuItem>
                    </Select>
                </FormControl>
            )
        }

        const renderSizeSelection = () => {
            return (
                <>
                    <TextField
                        label={t("file_size")}
                        value={file.size}
                        onChange={(e) => setFile({...file, size: e.target.value})}
                        fullWidth
                        type={"number"}
                        error={!isCorrectlySized}
                        InputProps={{
                            endAdornment: <Typography variant={"body2"}>{file.unit}</Typography>,
                            inputProps: {
                                max:
                                    file.unit === "px" ? 2500 :
                                        (file.unit === "mm" && file.resolution === "300") ? 212 :
                                            847,
                                min: file.unit === "px" ? 100 : 30,
                            }
                        }}
                        helperText={
                            file.unit === "px" ? t("file_size_alert_px") :
                                (file.unit === "mm" && file.resolution === "75") ? t("file_size_alert_75") :
                                    t("file_size_alert_300")
                        }
                    />

                    <Collapse in={file.unit === "mm"} sx={{ pt: 2 }}>
                        <Stack direction={"row"} spacing={2}>
                            <Typography>{t("resolution")}</Typography>
                            {
                                ["75", "300"].map((resolution: any) => (
                                    <Button
                                        variant={file.resolution === resolution ? "contained" : "outlined"}
                                        size={"small"}
                                        onClick={() => setFile({...file, resolution: resolution})}
                                        disableElevation
                                        key={resolution}
                                    >
                                        {resolution} ppi
                                    </Button>
                                ))
                            }
                        </Stack>
                    </Collapse>
                </>
            )
        }

        const renderDimXSelection = () => {
            return (
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Divider>
                            <Typography variant={"body2"}>
                                {t("gs1_options")}
                            </Typography>
                        </Divider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            size={"small"}
                            value={file.dim_x}
                            onChange={(e) => setFile({...file, dim_x: e.target.value})}
                            fullWidth
                            disabled={!file.real_size}
                            label={t("dimX")}
                            type={"number"}
                            inputProps={{
                                min: MIN_DIMX,
                                max: MAX_DIMX,
                                step: 0.005,
                            }}
                        />
                    </Grid>
                </Grid>
            )
        }

        const renderOptions = () => {
            if (downloadType === "gs1") return (
                <>
                    <Grid size={12}>
                        <Typography>
                            {t("gs1_download_intro")}
                        </Typography>
                    </Grid>
                    <Grid size={12}>{renderGs1FormatSelection()}</Grid>
                    <Grid size={12}>{renderDimXSelection()}</Grid>
                </>
            )
            else return (
                <>
                    <Grid size={12}>{renderTypeSelection()}</Grid>
                    {file.format === "pdf" && <Grid size={12}>{renderPdfContentSelection()}</Grid>}
                    <Grid size={12}>{renderUnitSelection()}</Grid>
                    <Grid size={12}>{renderSizeSelection()}</Grid>
                </>
            )
        }

        return (
            <PaperLabelled label={t("file_size_and_format")} sx={{ mt: 2 }}>
                <Grid container spacing={2} sx={{ pt: "4px" }}>
                    {isGs1Product && <Grid size={12}>{renderDownloadTypeSelector()}</Grid>}
                    {renderOptions()}
                </Grid>
            </PaperLabelled>
        )
    }


    return (
        <Dialog {...{open, onClose}} maxWidth={"sm"} fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1}>
                        {t("products:download_qrcodes")}
                        {isBulk && ` (${bulkProducts.length})`}
                    </Box>
                    <IconButton onClick={onClose} aria-label={"close"}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {isMixedSelection && (
                    <Alert severity={"warning"} sx={{ mt: 1 }}>
                        {t("products:download_mixed_types_warning")}
                    </Alert>
                )}
                {renderFileFormatSelection()}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button className={"defaultButton"} onClick={onClose}>
                    {t("common:back")}
                </Button>
                <Button
                    variant={"contained"}
                    disableElevation
                    onClick={() => downloadQRCode()}
                >
                    {t("products:download")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
